import {
  Route,
  createBrowserRouter,
  RouterProvider,
  createRoutesFromElements,
  Navigate,
  useLocation
} from "react-router-dom";
import { useSelector } from "react-redux";
import { Fragment } from "react";
// Layout
import AppLayout from "../../components/AppLayout";
// --- PAGES ---
import Welcome from "../../pages/Welcome";
// Explore Pages
import Explore from "../../pages/Explore";
import OfferDetails from "../../pages/Explore/OfferDetails";
import NotFound from "../../pages/NotFound";
// Booking Pages
import Booking from "../../pages/Booking";
// SBB
import Sbb from "../../pages/PublicTransport/Sbb";
// SBB Swiss Travel Pass
import SwissTravelPass from "../../pages/PublicTransport/Sbb/SwissTravelPass";
// Munich City Pass
import MunichCityPass from "../../pages/PublicTransport/MunichCityPass";
// Frankfurt Card
import FrankfurtCard from "../../pages/PublicTransport/FrankfurtCard";
// Uber
import Uber from "../../pages/PublicTransport/Uber";
// Airport Taxi
import AirportTaxi from "../../pages/PublicTransport/AirportTaxi";
// Europcar
import Europcar from "../../pages/PublicTransport/Europcar";
// LIMOS4
import Limos4 from "../../pages/PublicTransport/Limos4";
// CAT
import Cat from "../../pages/PublicTransport/Cat";
// ÖBB
import Obb from "../../pages/PublicTransport/Obb";
import AirportBus from "../../pages/PublicTransport/AirportBus";
import { lufthansaExpressBusConfig } from "../../pages/PublicTransport/AirportBus/providers/lufthansaExpressBus";
import { viennaAirportBusConfig } from "../../pages/PublicTransport/AirportBus/providers/viennaAirportBus";
// Deutsche Bahn
import DeutscheBahn from "../../pages/PublicTransport/DeutscheBahn";
// Shared
import BookingConfirmation from "../../pages/Booking/BookingConfirmation";
import AirlineLanding from "../../pages/AirlineLanding";
import TestPage from "../../pages/TestPage";
// helpers
import { checkIsDefaultDomain } from "../utils/helpers/subdomainHelpers/checkIsDefaultDomain";

export const baseUrl = "";

/** Keeps inbound query params (e.g. utm_source) when redirecting to a language-prefixed path. */
function PreserveSearchNavigate({ to }: { to: string }) {
  const { search } = useLocation();
  return <Navigate to={{ pathname: to, search }} replace />;
}

export const hotelOnlyRoutes: Array<any> = [
  {
    path: baseUrl,
    element: <Explore />
  },
  {
    path: "/offers/:code/",
    element: <OfferDetails />
  },
  {
    path: "/offers/:code/checkout/",
    element: <Booking />
  },
  {
    path: "/offers/:code/checkout/confirmation",
    element: <BookingConfirmation />
  }
];

export const transportRoutes: Array<any> = [
  {
    path: "/transport/sbb/",
    element: <Sbb />
  },
  {
    path: "/transport/swiss_travel_pass/",
    element: <SwissTravelPass />
  },
  {
    path: "/transport/munich-city-pass/",
    element: <MunichCityPass />
  },
  {
    path: "/transport/frankfurt-card/",
    element: <FrankfurtCard />
  },
  {
    path: "/transport/zurich_card/",
    element: <Sbb isZurichCard={true} />
  },
  {
    path: "/transport/zurich_card/confirmation",
    element: <BookingConfirmation />
  },
  {
    path: "/transport/uber/",
    element: <Uber />
  },
  {
    path: "/transport/airport-taxi/",
    element: <AirportTaxi />
  },
  {
    path: "/transport/europcar/",
    element: <Europcar />
  },
  {
    path: "/transport/limos4/",
    element: <Limos4 />
  },
  {
    path: "/transport/cat/",
    element: <Cat />
  },
  {
    path: "/transport/obb/",
    element: <Obb />
  },
  {
    path: "/transport/vienna-airport-bus/",
    element: <AirportBus config={viennaAirportBusConfig} />
  },
  {
    path: "/transport/lufthansa-express-bus/",
    element: <AirportBus config={lufthansaExpressBusConfig} />
  },
  {
    path: "/transport/deutsche-bahn/",
    element: <DeutscheBahn />
  },
  {
    path: "/transport/limos4/confirmation",
    element: <BookingConfirmation />
  },
  {
    path: "/transport/cat/confirmation",
    element: <BookingConfirmation />
  },
  {
    path: "/transport/obb/confirmation",
    element: <BookingConfirmation />
  },
  {
    path: "/transport/vienna-airport-bus/confirmation",
    element: <BookingConfirmation />
  },
  {
    path: "/transport/lufthansa-express-bus/confirmation",
    element: <BookingConfirmation />
  },
  {
    path: "/transport/deutsche-bahn/confirmation",
    element: <BookingConfirmation />
  },
  {
    path: "/transport/europcar/confirmation",
    element: <BookingConfirmation />
  },
  {
    path: "/transport/uber/confirmation",
    element: <BookingConfirmation />
  },
  {
    path: "/transport/sbb/confirmation",
    element: <BookingConfirmation />
  },
  {
    path: "/transport/swiss_travel_pass/confirmation",
    element: <BookingConfirmation />
  },
  {
    path: "/transport/munich-city-pass/confirmation",
    element: <BookingConfirmation />
  },
  {
    path: "/transport/frankfurt-card/confirmation",
    element: <BookingConfirmation />
  },
  {
    path: "/transport/airport-taxi/confirmation",
    element: <BookingConfirmation />
  }
];

export const airlineOnlyRoutes: Array<any> = [
  {
    path: baseUrl,
    element: <AirlineLanding />
  }
];

export const mainRoutes: Array<any> = [...hotelOnlyRoutes, ...transportRoutes];

function AppRouter() {
  // states
  const defaultDomain = useSelector((state: any) => state.global.defaultDomain);
  const platformType = useSelector((state: any) => state.global.platformType);

  const language = localStorage.getItem("i18nextLng") || "en";
  const isDefaultDomain = checkIsDefaultDomain(window.location.hostname, defaultDomain);

  const getSubdomainRoutes = () => {
    if (platformType === "airline") {
      return [...airlineOnlyRoutes, ...transportRoutes];
    }
    return [...hotelOnlyRoutes, ...transportRoutes];
  };

  return (
    <RouterProvider
      router={createBrowserRouter(
        createRoutesFromElements(
          <Route path={"/"} element={<AppLayout />}>
            {isDefaultDomain ? (
              <>
                <Route path={baseUrl + "/:language"} element={<Welcome />} />
                <Route path="/" element={<PreserveSearchNavigate to={`/${language}`} />} />
              </>
            ) : (
              <>
                <Route index element={<PreserveSearchNavigate to={`/${language}`} />} />
                {getSubdomainRoutes()?.map(route => {
                  return (
                    <Fragment key={route.path}>
                      <Route key={route.path} path={route.path} element={route.element} />
                      <Route key={route.path} path={"/:language" + route.path} element={route.element} />
                      {route.path ? (
                        <Route
                          path={route.path}
                          element={<PreserveSearchNavigate to={"/" + language + route.path} />}
                        />
                      ) : null}
                    </Fragment>
                  );
                })}
              </>
            )}

            {/* 👇️ only match this when no other routes match */}
            <Route path={`/*`} element={<NotFound />} />
            {/* 👇️ test page */}
            <Route path={`${baseUrl}/get-local-test-page`} element={<TestPage />} />
          </Route>
        )
      )}
    />
  );
}

export default AppRouter;
